/**
 * Админ-панель ChronoGuessr.
 *
 * Отдельное SPA на admin.chronoguessr.io, работающее с тем же backend'ом,
 * что и игровой фронтенд, но только с его admin-эндпоинтами. Своего сервиса
 * и своей базы у админки нет: это принципиально — один источник правды,
 * один набор моделей, одна авторизация с проверкой роли.
 */
import { Refine, Authenticated } from "@refinedev/core";
import {
  ErrorComponent,
  ThemedLayout,
  ThemedTitle,
  useNotificationProvider,
} from "@refinedev/antd";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { App as AntdApp, ConfigProvider, theme } from "antd";
import ruRU from "antd/locale/ru_RU";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";

import "@refinedev/antd/dist/reset.css";

import { LoginPage } from "./pages/login";
import { MetricsPage } from "./pages/metrics";
import { PhotoCreate, PhotoEdit } from "./pages/photos/form";
import { PhotoList } from "./pages/photos/list";
import { PhotoShow } from "./pages/photos/show";
import { QuestCreate, QuestEdit } from "./pages/quests/form";
import { QuestList } from "./pages/quests/list";
import { QuestShow } from "./pages/quests/show";
import { TagList } from "./pages/tags/list";
import { AnnouncementList } from "./pages/announcements/list";
import { LandingPhotoList } from "./pages/landing/list";
import { AnnouncementCreate, AnnouncementEdit } from "./pages/announcements/form";
import { FeedbackList } from "./pages/feedback/list";
import { FeedbackShow } from "./pages/feedback/show";
import { useNewFeedbackCount } from "./hooks/useNewFeedbackCount";
import { UserList } from "./pages/users/list";
import { authProvider } from "./providers/authProvider";
import { i18nProvider } from "./providers/i18nProvider";
import { dataProvider } from "./providers/dataProvider";

/**
 * Базовый путь приложения. Совпадает с base сборки: при отдаче панели
 * подпутём /admin/ роутер должен знать об этом префиксе, иначе все ссылки
 * будут вести в корень домена, где живёт игровой фронтенд.
 */
const basename = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function App() {
  return (
    <BrowserRouter basename={basename}>
      <ConfigProvider
        locale={ruRU}
        theme={{
          // Тёмная тема с тем же янтарным акцентом, что и в игре: админка
          // должна выглядеть частью того же продукта.
          algorithm: theme.darkAlgorithm,
          token: {
            colorPrimary: "#d99447",
            colorBgBase: "#0c0d11",
            colorBgContainer: "#161920",
            colorBorder: "#262b36",
            borderRadius: 8,
            // Та же гарнитура, что и в игре: панель и игра должны выглядеть
            // одним продуктом, а не двумя разными.
            fontFamily:
              "Manrope, system-ui, -apple-system, 'Segoe UI', sans-serif",
          },
        }}
      >
        <AntdApp>
          <AdminRefine />
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}

/**
 * Внутренняя часть приложения. Вынесена отдельным компонентом, потому что
 * useNotificationProvider обязан вызываться внутри <AntdApp>: иначе всплывающие
 * уведомления рендерятся вне контекста темы и antd об этом предупреждает.
 */
function AdminRefine() {
  const notificationProvider = useNotificationProvider();
  // Счётчик прямо в подписи пункта меню: meta.label у Refine — строка, и
  // вложить туда бейдж нельзя, а «(3)» рядом с названием читается сразу и
  // не требует своего компонента поверх вёрстки библиотеки.
  const newFeedback = useNewFeedbackCount();

  return (
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            routerProvider={routerProvider}
            notificationProvider={notificationProvider}
            i18nProvider={i18nProvider}
            resources={[
              {
                name: "photos",
                list: "/photos",
                create: "/photos/create",
                edit: "/photos/edit/:id",
                show: "/photos/show/:id",
                meta: { label: "Фотографии", canDelete: true },
              },
              {
                name: "quests",
                list: "/quests",
                create: "/quests/create",
                edit: "/quests/edit/:id",
                show: "/quests/show/:id",
                meta: { label: "Квесты", canDelete: true },
              },
              {
                name: "tags",
                list: "/tags",
                meta: { label: "Теги", canDelete: true },
              },
              {
                name: "feedback",
                list: "/feedback",
                show: "/feedback/show/:id",
                meta: {
                  label: newFeedback > 0 ? `Сообщения (${newFeedback})` : "Сообщения",
                },
              },
              {
                name: "announcements",
                list: "/announcements",
                create: "/announcements/create",
                edit: "/announcements/edit/:id",
                meta: { label: "Анонсы", canDelete: true },
              },
              {
                name: "landing-photos",
                list: "/landing-photos",
                meta: { label: "Первый экран", canDelete: true },
              },
              {
                name: "users",
                list: "/users",
                meta: { label: "Пользователи" },
              },
              {
                name: "metrics",
                list: "/metrics",
                meta: { label: "Метрики" },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              disableTelemetry: true,
              title: { text: "ChronoGuessr Admin" },
            }}
          >
            <Routes>
              {/* Всё содержимое панели закрыто: доступ только модераторам и админам. */}
              <Route
                element={
                  <Authenticated key="authenticated" fallback={<CatchAllNavigate to="/login" />}>
                    <ThemedLayout
                      Title={({ collapsed }) => (
                        <ThemedTitle
                          collapsed={collapsed}
                          text="ChronoGuessr"
                          icon={<img src={`${import.meta.env.BASE_URL}logo-mark.svg`} width={24} height={24} alt="" />}
                        />
                      )}
                    >
                      <Outlet />
                    </ThemedLayout>
                  </Authenticated>
                }
              >
                <Route index element={<NavigateToResource resource="photos" />} />

                <Route path="/photos">
                  <Route index element={<PhotoList />} />
                  <Route path="create" element={<PhotoCreate />} />
                  <Route path="edit/:id" element={<PhotoEdit />} />
                  <Route path="show/:id" element={<PhotoShow />} />
                </Route>

                <Route path="/quests">
                  <Route index element={<QuestList />} />
                  <Route path="create" element={<QuestCreate />} />
                  <Route path="edit/:id" element={<QuestEdit />} />
                  <Route path="show/:id" element={<QuestShow />} />
                </Route>

                <Route path="/tags">
                  <Route index element={<TagList />} />
                </Route>

                <Route path="/announcements">
                  <Route index element={<AnnouncementList />} />
                  <Route path="create" element={<AnnouncementCreate />} />
                  <Route path="edit/:id" element={<AnnouncementEdit />} />
                </Route>

                <Route path="/landing-photos">
                  <Route index element={<LandingPhotoList />} />
                </Route>

                <Route path="/feedback">
                  <Route index element={<FeedbackList />} />
                  <Route path="show/:id" element={<FeedbackShow />} />
                </Route>

                <Route path="/users">
                  <Route index element={<UserList />} />
                </Route>

                <Route path="/metrics" element={<MetricsPage />} />

                <Route path="*" element={<ErrorComponent />} />
              </Route>

              <Route
                element={
                  <Authenticated key="unauthenticated" fallback={<Outlet />}>
                    <NavigateToResource resource="photos" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<LoginPage />} />
              </Route>
            </Routes>

            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
  );
}
